import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, XCircle, ArrowRight } from "lucide-react";
import HypayPaymentForm from "../components/HypayPaymentForm";
import { useCartStore } from "../stores/useCartStore";
import { useUserStore } from "../stores/useUserStore";
import { getImageUrl } from "../lib/imageUtils";

const IsraeliPaymentPage = () => {
  const navigate = useNavigate();
  const { cart, clearCart, total, subtotal, shippingCost } = useCartStore();
  const { user } = useUserStore();
  const [paymentStatus, setPaymentStatus] = useState("form"); // form, processing, success, error
  const [paymentResult, setPaymentResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  // המחירים כוללים מע"מ של 18% — משתמשים בסכומים מהסטור (כולל משלוח והנחות)
  const subtotalBeforeVAT = (total || 0) / 1.18; // מחיר לפני מע"מ
  const vat = (total || 0) - subtotalBeforeVAT;  // מע"מ 18%

  // בדיקה שיש פריטים בעגלה
  useEffect(() => {
    if (cart.length === 0) {
      navigate("/cart");
    }
  }, [cart, navigate]);

  // בדיקה שהמשתמש מחובר
  useEffect(() => {
    if (!user) {
      navigate("/login");
    }
  }, [user, navigate]);

  // טיפול בהצלחת תשלום
  const handlePaymentSuccess = (result) => {
    setPaymentStatus("success");
    setPaymentResult(result);
    
    // ניקוי עגלה לאחר תשלום מוצלח
    setTimeout(() => {
      clearCart();
    }, 3000);
  };

  // טיפול בשגיאת תשלום
  const handlePaymentError = (error) => {
    setPaymentStatus("error");
    setErrorMessage(error);
  };

  // אם אין פריטים בעגלה
  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">העגלה ריקה</h2>
          <p className="text-gray-600 mb-6">אין פריטים לתשלום</p>
          <button
            onClick={() => navigate("/")}
            className="bg-gradient-to-r from-[#9a624b] to-[#ac609c] text-white px-6 py-3 rounded-lg hover:opacity-90 transition-opacity"
          >
            חזור לחנות
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-8 px-4">
      <div className="max-w-7xl mx-auto">
        
        {paymentStatus === "form" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            {/* כותרת */}
            <div className="text-center">
              <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-[#9a624b] via-[#ac609c] to-[#9a624b] bg-clip-text text-transparent mb-4">
                השלמת התשלום
              </h1>
              <p className="text-gray-600 text-lg">
                שלב אחרון לרכישת הפריטים שלך
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {/* סיכום הזמנה */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="lg:col-span-1"
              >
                <div className="bg-white rounded-2xl p-6 border border-gray-200 sticky top-4">
                  <h3 className="text-xl font-bold text-[#9a624b] mb-6">סיכום הזמנה</h3>
                  
                  {/* פריטים */}
                  <div className="space-y-4 mb-6">
                    {cart && Array.isArray(cart) && cart.map((item) => {
                      return (
                        <div key={item._id} className="flex items-center space-x-3">
                          <div className="w-16 h-16 bg-[#320619] rounded-lg flex items-center justify-center">
                            { (item.image || item.product?.imageUrl || (Array.isArray(item.product?.images) && item.product.images[0])) ? (
                              <img
                                src={getImageUrl(item.image || item.product?.imageUrl || (Array.isArray(item.product?.images) ? item.product.images[0] : undefined))}
                                alt={(item.name || item.product?.name || '')}
                                className="w-full h-full object-cover rounded-lg"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }}
                              />
                            ) : null}
                            {!(item.image || item.product?.imageUrl || (Array.isArray(item.product?.images) && item.product.images[0])) && (
                              <div className="text-[#8b6f7c] text-xs">תמונה</div>
                            )}
                                                        <div className="text-[#8b6f7c] text-xs" style={{display: 'none'}}>תמונה</div>
                          </div>
                          <div className="flex-1">
                            <h4 className="text-gray-900 font-medium">{item.name || item.product?.name}</h4>
                            <p className="text-[#8b6f7c] text-sm">כמות: {item.quantity}</p>
                          </div>
                          <div className="text-[#ac609c] font-bold">
                            {(() => { const p = (typeof item.price === 'number' ? item.price : (item.product?.price || 0)); return `₪${(p * (item.quantity || 0)).toFixed(2)}`; })()}
                          </div>
                        </div>
                      );
                    })}
                    </div>

                  {/* חישוב סכומים */}
                  <div className="border-t border-[#5b0c3d] pt-4 space-y-2">
                    <div className="flex justify-between text-gray-600">
                      <span>משלוח:</span>
                      <span>{(shippingCost || 0) === 0 ? 'חינם' : `₪${Number(shippingCost).toFixed(2)}`}</span>
                    </div>
                    <div className="flex justify-between text-gray-600">
                      <span>מחיר לפני מע&quot;מ:</span>
                      <span>₪{subtotalBeforeVAT.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-gray-600">
                      						<span>מע&quot;מ (18%):</span>
                      <span>₪{vat.toFixed(2)}</span>
                    </div>
                    <div className="border-t border-[#5b0c3d] pt-2">
                      <div className="flex justify-between text-gray-900 font-bold text-lg">
                        <span>סה&quot;כ כולל מע&quot;מ:</span>
                        <span className="text-[#ac609c]">₪{total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  {/* מידע נוסף */}
                  <div className="mt-6 p-4 bg-[#320619] rounded-lg">
                    <h4 className="text-[#9a624b] font-bold mb-2">הערות חשובות:</h4>
                    <ul className="text-gray-600 text-sm space-y-1">
                      <li>• משלוח חינם מעל 299₪</li>
                      <li>• זמן אספקה: 3–7 ימי עסקים</li>
                      <li>• אחריות מלאה למוצר</li>
                      <li>• אפשרות החזרה תוך 14 יום</li>
                    </ul>
                  </div>
                </div>
              </motion.div>

              {/* טופס תשלום */}
              <div className="lg:col-span-2">
                <HypayPaymentForm
                  totalAmount={total}
                  onPaymentSuccess={handlePaymentSuccess}
                  onPaymentError={handlePaymentError}
                  items={cart}
                />
              </div>
            </div>
          </motion.div>
        )}

        {paymentStatus === "success" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-2xl mx-auto text-center"
          >
            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-white" />
              </div>
              
              <h2 className="text-3xl font-bold text-[#9a624b] mb-4">
                התשלום בוצע בהצלחה!
              </h2>
              
              <p className="text-gray-900 text-lg mb-6">
                תודה על הרכישה! פרטי ההזמנה נשלחו למייל שלך
              </p>

              {paymentResult && (
                <div className="bg-[#320619] rounded-lg p-4 mb-6 text-right">
                  <h3 className="text-[#ac609c] font-bold mb-2">פרטי התשלום:</h3>
                  <div className="space-y-1 text-gray-600">
                    <p>מספר עסקה: <span className="text-gray-900">{paymentResult.transactionId}</span></p>
                    {paymentResult.approvalNumber && (
                      <p>מספר אישור: <span className="text-gray-900">{paymentResult.approvalNumber}</span></p>
                    )}
                    <p>סכום: <span className="text-gray-900">₪{total.toFixed(2)}</span></p>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate("/")}
                  className="bg-gradient-to-r from-[#9a624b] to-[#ac609c] text-white px-6 py-3 rounded-lg hover:opacity-90 transition-opacity"
                >
                  המשך קנייה
                </button>
                <button
                  onClick={() => navigate("/profile")}
                  className="bg-[#6a5e5a] text-gray-900 px-6 py-3 rounded-lg hover:bg-[#8b6f7c] transition-colors flex items-center justify-center space-x-2"
                >
                  <span>הפרופיל שלי</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {paymentStatus === "error" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-2xl mx-auto text-center"
          >
            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-12 h-12 text-white" />
              </div>
              
              <h2 className="text-3xl font-bold text-red-400 mb-4">
                שגיאה בתשלום
              </h2>
              
              <p className="text-gray-900 text-lg mb-4">
                {errorMessage || "אירעה שגיאה בעיבוד התשלום"}
              </p>

              <p className="text-gray-600 mb-6">
                אנא נסה שוב או פנה אלינו לעזרה
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => setPaymentStatus("form")}
                  className="bg-gradient-to-r from-[#9a624b] to-[#ac609c] text-white px-6 py-3 rounded-lg hover:opacity-90 transition-opacity"
                >
                  נסה שוב
                </button>
                <button
                  onClick={() => navigate("/cart")}
                  className="bg-[#6a5e5a] text-gray-900 px-6 py-3 rounded-lg hover:bg-[#8b6f7c] transition-colors"
                >
                  חזור לעגלה
                </button>
              </div>

              {/* מידע ליצירת קשר */}
              <div className="mt-6 p-4 bg-[#320619] rounded-lg">
                <h4 className="text-[#9a624b] font-bold mb-2">זקוק לעזרה?</h4>
                <div className="text-gray-600 text-sm space-y-1">
                  <p>📞 טלפון: 053-3332127</p>
                  <p>📧 אימייל: nataly@sha-sha.co.il</p>
                  <p>🕒 שעות פעילות: א-ה 09:00-19:00, ו 09:00-14:00</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default IsraeliPaymentPage; 